/** CDR browser table component with TanStack Table. */
import React, { useState, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  Skeleton,
  Toolbar,
  alpha,
  Button,
  LinearProgress,
} from "@mui/material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  createColumnHelper,
  SortingState,
  flexRender,
} from "@tanstack/react-table";
import { Cdr } from "../../types/cdr";
import { format } from "date-fns";
import { formatDuration, formatCurrency } from "../../utils/format";
import checkboxColumn from "../tables/CheckboxColumn";
import ColumnVisibilityMenu from "../tables/ColumnVisibilityMenu";

interface CdrBrowserProps {
  cdrs: Cdr[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  onSelectAllMatching?: () => Promise<{ ids: string[]; total_count: number }>;
  /** Row click (outside the checkbox column) opens the CDR detail dialog. */
  onRowClick?: (callId: string) => void;
  isReseller?: boolean;
  currentUserId?: string;
  /** D2: controlled sorting — when provided, sorting is applied server-side
   * (manualSorting) and header clicks bubble up via onSortingChange. */
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
}

/** Column helper for TanStack Table. */
const columnHelper = createColumnHelper<Cdr>();

/** Get status chip color based on hangup cause. */
function getStatusChip(hangupCause: string | null) {
  if (!hangupCause) {
    return <Chip label="Unknown" size="small" color="default" />;
  }

  const isSuccess = hangupCause === "NORMAL_CLEARING";
  return (
    <Chip
      label={hangupCause}
      size="small"
      color={isSuccess ? "success" : "error"}
    />
  );
}

/** CDR table component with sorting and pagination. */
export default function CdrBrowser({
  cdrs,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  onSelectionChange,
  onSelectAllMatching,
  onRowClick,
  isReseller = false,
  currentUserId,
  sorting: controlledSorting,
  onSortingChange,
}: CdrBrowserProps) {
  const [internalSorting, setInternalSorting] = useState<SortingState>([
    { id: "start_time", desc: true },
  ]);
  const sorting = controlledSorting !== undefined ? controlledSorting : internalSorting;
  const serverSorted = controlledSorting !== undefined;
  const [rowSelection, setRowSelection] = useState({});
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [allMatchingCount, setAllMatchingCount] = useState<number | null>(null);

  /** Define column definitions for visibility menu. */
  const columnDefinitions = useMemo(
    () => [
      { id: "call_id", header: "Call ID" },
      { id: "account", header: "Account" },
      { id: "caller", header: "Caller" },
      { id: "callee", header: "Callee" },
      { id: "answer_time", header: "Answer Time" },
      { id: "duration", header: "Duration" },
      { id: "billsec", header: "Billable" },
      { id: "margin_breakdown", header: "Margin" },
      { id: "hangup_cause", header: "Status" },
      { id: "carrier_ip", header: "Carrier IP" },
      { id: "start_time", header: "Start Time" },
      { id: "end_time", header: "End Time" },
    ],
    []
  );

  /** Define table columns. */
  const columns = useMemo(
    () => [
      checkboxColumn,
      columnHelper.accessor("call_id", {
        header: "Call ID",
        cell: (info) => (
          <Tooltip title={info.getValue()}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                maxWidth: 150,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {info.getValue()}
            </Typography>
          </Tooltip>
        ),
      }),
      columnHelper.accessor("account", {
        header: "Account",
        cell: (info) => {
          const account = info.getValue();
          if (!account) return "-";
          return (
            <Tooltip title={account}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  maxWidth: 150,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {account}
              </Typography>
            </Tooltip>
          );
        },
      }),
      columnHelper.accessor("caller", {
        header: "Caller",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("callee", {
        header: "Callee",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("answer_time", {
        header: "Answer Time",
        cell: (info) => {
          const value = info.getValue();
          return value
            ? format(new Date(value), "yyyy-MM-dd HH:mm:ss")
            : "-";
        },
      }),
      columnHelper.accessor("duration", {
        header: "Duration",
        cell: (info) => formatDuration(info.getValue()),
      }),
      columnHelper.accessor("billsec", {
        header: "Billable",
        cell: (info) => formatDuration(info.getValue()),
      }),
      columnHelper.accessor("margin_breakdown", {
        header: "Margin",
        cell: (info) => {
          const breakdown = info.getValue();
          if (!breakdown) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="caption" display="block">
                {formatCurrency(breakdown.cost, 4)}
              </Typography>
              {breakdown.margin_amount != null && (
                <Typography
                  variant="caption"
                  display="block"
                  color="success.main"
                >
                  +{formatCurrency(breakdown.margin_amount, 4)}
                </Typography>
              )}
            </Box>
          );
        },
      }),
      columnHelper.accessor("hangup_cause", {
        header: "Status",
        cell: (info) => getStatusChip(info.getValue()),
      }),
      columnHelper.accessor("carrier_ip", {
        header: "Carrier IP",
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("start_time", {
        header: "Start Time",
        cell: (info) =>
          format(new Date(info.getValue()), "yyyy-MM-dd HH:mm:ss"),
      }),
      columnHelper.accessor("end_time", {
        header: "End Time",
        cell: (info) =>
          format(new Date(info.getValue()), "yyyy-MM-dd HH:mm:ss"),
      }),
    ],
    [isReseller, columnDefinitions]
  );

  /** Create table instance with row selection. */
  const table = useReactTable({
    data: cdrs,
    columns,
    state: {
      sorting,
      rowSelection,
    },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      if (onSortingChange) {
        onSortingChange(next);
      } else {
        setInternalSorting(next);
      }
    },
    // D2: server-sorted mode keeps the API row order (no client reorder)
    manualSorting: serverSorted,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    ...(serverSorted ? {} : { getSortedRowModel: getSortedRowModel() }),
  });

  /** Get selected row IDs and pass to parent. */
  const selectedRowIds = useMemo(
    () => table.getSelectedRowModel().flatRows.map((row) => row.original.call_id),
    [table]
  );

  /** Notify parent of selection changes. */
  React.useEffect(() => {
    onSelectionChange?.(selectedRowIds);
  }, [selectedRowIds, onSelectionChange]);

  /** Handle page change. */
  const handleChangePage = (_event: unknown, newPage: number) => {
    onPageChange(newPage + 1, pageSize);
  };

  /** Handle rows per page change. */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    onPageChange(1, newPageSize);
  };

  /** Handle select all matching CDRs. */
  const handleSelectAllMatching = async () => {
    if (!onSelectAllMatching) return;

    setIsSelectingAll(true);
    try {
      const result = await onSelectAllMatching();

      // Update row selection with all matching IDs
      const newSelection: Record<string, boolean> = {};
      result.ids.forEach((id) => {
        newSelection[id] = true;
      });
      setRowSelection(newSelection);
      setAllMatchingCount(result.total_count);
    } catch (error) {
      console.error("Failed to select all matching:", error);
    } finally {
      setIsSelectingAll(false);
    }
  };

  /** Handle clear selection. */
  const handleClearSelection = () => {
    setRowSelection({});
    setAllMatchingCount(null);
  };

  /** Render loading skeleton — table-shaped so layout matches content (Wave 3.3). */
  if (loading) {
    const skeletonColumns = 8;
    return (
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {Array.from({ length: skeletonColumns }).map((_, i) => (
                  <TableCell key={i}>
                    <Skeleton variant="text" width={70} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 10 }).map((_, r) => (
                <TableRow key={r}>
                  {Array.from({ length: skeletonColumns }).map((_, c) => (
                    <TableCell key={c}>
                      <Skeleton variant="text" width={c === 0 ? 120 : 60 + ((r * 13 + c * 29) % 60)} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  /** Render empty state. */
  if (cdrs.length === 0) {
    return (
      <Paper sx={{ width: "100%", p: 4, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          No CDRs found for selected filters
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      {/* Progress bar during bulk selection */}
      {isSelectingAll && <LinearProgress />}

      {/* Toolbar with column visibility menu */}
      <Toolbar
        variant="dense"
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          justifyContent: "flex-end",
          ...(selectedRowIds.length > 0 && {
            bgcolor: (theme) =>
              alpha(
                theme.palette.primary.main,
                theme.palette.action.selectedOpacity
              ),
          }),
        }}
      >
        {selectedRowIds.length > 0 ? (
          <Box sx={{ flex: "1 1 100%", display: "flex", alignItems: "center", gap: 1 }}>
            {isSelectingAll ? (
              <Typography variant="body2">
                Selecting all matching CDRs...
              </Typography>
            ) : allMatchingCount !== null ? (
              <Typography variant="body2">
                All {allMatchingCount.toLocaleString()} matching CDRs selected
              </Typography>
            ) : (
              <Typography variant="body2">
                {selectedRowIds.length} CDR{selectedRowIds.length !== 1 ? "s" : ""} selected
              </Typography>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={handleClearSelection}
              disabled={isSelectingAll}
            >
              Clear selection
            </Button>
            {onSelectAllMatching && allMatchingCount === null && (
              <Button
                size="small"
                variant="contained"
                onClick={handleSelectAllMatching}
                disabled={isSelectingAll}
              >
                Select all {total} matching
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ flex: "1 1 100%" }} />
        )}
        <ColumnVisibilityMenu
          table={table}
          columns={columnDefinitions}
          storageKey="cdrs_columnVisibility"
        />
      </Toolbar>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, colIndex) => (
                  <TableCell
                    key={header.id}
                    aria-sort={
                      header.column.getIsSorted() === "asc"
                        ? "ascending"
                        : header.column.getIsSorted() === "desc"
                        ? "descending"
                        : undefined
                    }
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "background.paper",
                      // Wave 3.5: freeze the first column (review §3.1)
                      ...(colIndex === 0
                        ? { position: "sticky", left: 0, zIndex: 3, borderRight: "1px solid", borderColor: "divider" }
                        : {}),
                    }}
                  >
                    {header.column.getCanSort() ? (
                      <TableSortLabel
                        active={header.column.getIsSorted() !== false}
                        direction={
                          header.column.getIsSorted() === "asc"
                            ? "asc"
                            : "desc"
                        }
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableSortLabel>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original.call_id) : undefined}
                sx={{
                  "&:nth-of-type(odd)": { backgroundColor: "action.hover" },
                  ...(onRowClick
                    ? { cursor: "pointer" }
                    : {}),
                }}
              >
                {row.getVisibleCells().map((cell, colIndex) => {
                  // Wave 3.5 (review §3.1): long plain values get a hover
                  // tooltip; custom-rendered cells keep their markup.
                  const value = cell.getValue();
                  const isPlain = typeof value === "string" || typeof value === "number";
                  const cellContent = flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                  );
                  return (
                    <TableCell
                      key={cell.id}
                      onClick={
                        colIndex === 0
                          ? (e) => e.stopPropagation() // checkbox clicks select, don't open detail
                          : undefined
                      }
                      sx={
                        colIndex === 0
                          ? {
                              position: "sticky",
                              left: 0,
                              backgroundColor: "background.paper",
                              borderRight: "1px solid",
                              borderColor: "divider",
                            }
                          : undefined
                      }
                    >
                      {isPlain ? (
                        <Tooltip
                          title={String(value)}
                          enterDelay={400}
                          disableHoverListener={String(value ?? "").length < 20}
                        >
                          <Box component="span">{cellContent}</Box>
                        </Tooltip>
                      ) : (
                        cellContent
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={total}
        rowsPerPage={pageSize}
        page={page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelDisplayedRows={({ from, to, count }) =>
          `Showing ${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
        }
      />
    </Paper>
  );
}
